import { useEffect } from "react";

const CHATBOT_SCRIPT_SRC = "https://chatbot.theandyapp.com/static/widget-pro.js";
const CHATBOT_SCRIPT_SELECTOR = `script[src*="widget-pro.js"]`;
const CHATBOT_OVERRIDES_ID = "andy-chatbot-viewport-overrides";

/*
  <script
    src="https://chatbot.theandyapp.com/static/widget-pro.js"
    data-bot-id="bot1"
    data-title="Andy AI"
    data-color="#22c55e">
  </script>
*/

const CHATBOT_VIEWPORT_OVERRIDES = `
.ap__panel {
  width: min(400px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  height: min(640px, calc(100dvh - 112px));
  max-height: calc(100dvh - 112px);
  right: max(16px, env(safe-area-inset-right, 0px));
  bottom: max(96px, calc(68px + 16px + env(safe-area-inset-bottom, 0px)));
  top: auto;
}

.ap__bubble {
  right: max(16px, env(safe-area-inset-right, 0px));
  bottom: max(24px, env(safe-area-inset-bottom, 0px));
}

.ap__wrap,
.ap__wrap * {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
}

/* Widget appends a bare file input to body for uploads — hide it completely without breaking programmatic .click() */
body > input[type="file"],
input[type="file"][accept*="image/*,.pdf"],
input[type="file"].ap__file-input {
  position: fixed !important;
  top: -9999px !important;
  left: -9999px !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  overflow: hidden !important;
  pointer-events: none !important;
  z-index: -9999 !important;
}

@media (max-width: 480px) {
  .ap__panel {
    width: calc(100vw - 24px);
    max-width: calc(100vw - 24px);
    height: calc(100dvh - 96px);
    max-height: calc(100dvh - 96px);
    right: max(12px, env(safe-area-inset-right, 0px));
    bottom: max(76px, calc(60px + 16px + env(safe-area-inset-bottom, 0px)));
  }

  .ap__bubble {
    right: max(12px, env(safe-area-inset-right, 0px));
    bottom: max(16px, env(safe-area-inset-bottom, 0px));
  }
}
`;

const injectChatbotViewportOverrides = () => {
  if (document.getElementById(CHATBOT_OVERRIDES_ID)) return;

  const style = document.createElement("style");
  style.id = CHATBOT_OVERRIDES_ID;
  style.textContent = CHATBOT_VIEWPORT_OVERRIDES;
  document.head.appendChild(style);
};

const hideBareFileInputs = () => {
  const inputs = document.querySelectorAll<HTMLInputElement>(
    'body > input[type="file"], input[type="file"][accept*="image/*,.pdf"]'
  );
  inputs.forEach((input) => {
    input.style.position = "fixed";
    input.style.top = "-9999px";
    input.style.left = "-9999px";
    input.style.width = "0px";
    input.style.height = "0px";
    input.style.opacity = "0";
    input.style.pointerEvents = "none";
    input.style.overflow = "hidden";
    input.style.zIndex = "-9999";
  });
};

export const ChatbotWidget = () => {
  useEffect(() => {
    // Always inject overrides immediately
    injectChatbotViewportOverrides();
    hideBareFileInputs();

    // Observe body for any bare file input dynamically appended by widget script
    const observer = new MutationObserver(() => {
      hideBareFileInputs();
    });

    observer.observe(document.body, { childList: true, subtree: false });

    const existingScript = document.querySelector(CHATBOT_SCRIPT_SELECTOR);

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = CHATBOT_SCRIPT_SRC;
      script.setAttribute("data-bot-id", "bot1");
      script.setAttribute("data-title", "Andy AI");
      script.setAttribute("data-color", "#22c55e");
      script.async = true;
      script.onload = () => {
        injectChatbotViewportOverrides();
        hideBareFileInputs();
      };
      document.body.appendChild(script);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return null;
};
